import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { FiLock, FiUser } from 'react-icons/fi';
import { GoGear } from 'react-icons/go';
import { RiArrowDownSLine } from 'react-icons/ri';
import { Link, NavLink, useLocation } from 'react-router-dom';

import logoInk from '@/assets/vh-black-name.svg';
import logoWhite from '@/assets/vh-white-name.svg';
import { useRoles } from '@/hooks/useRoles';
import type { Permission } from '@/permissions/permissions.catalog';
import { useAccessGate } from '@/permissions/useAccessGate';
import { usePermissions } from '@/permissions/usePermissions';
import { getVisibleNavGroups } from '@/router/routes.config';
import { useDrawerStore } from '@/store/drawer/drawer.store';
import { useNotificationContext } from '@/store/notifications/use-notifications-context';
import { usePerson } from '@/store/persons/person.store';
import { useCurrentServiceProvider } from '@/store/service-providers/current-service-provider.store';
import { useAccountServiceProviderQuery } from '@/store/service-providers/service-providers.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { UserRole } from '@/utils/roles';
import { useHasHover } from '@hooks/useHasHover.tsx';
import { useIsMobile } from '@hooks/useIsMobile.tsx';

import { DesktopSidebarRail } from './DesktopSidebarRail';
import styles from './Drawer.module.scss';

export { DesktopSidebarRail } from './DesktopSidebarRail';

type FooterItem = {
  key: string;
  label: string;
  icon: ReactNode;
  to: string;
};

const Drawer = () => {
  const isMobile = useIsMobile();
  const hasHover = useHasHover();
  const isDrawerOpen = useDrawerStore((s) => s.isOpen);
  const toggleDrawer = useDrawerStore((s) => s.toggle);
  const closeDrawer = useDrawerStore((s) => s.close);
  const { primaryRole, userRoles, isGuestAudience } = useRoles();
  const { can, canAny } = usePermissions();
  const { verdictFor } = useAccessGate();
  const { me, currentUser } = usePerson();
  const { vessel } = useVessels();
  const selectedServiceProviderId = useCurrentServiceProvider((s) => s.selectedServiceProviderId);
  useEffect(() => {
    void me();
  }, [me]);

  // ponytail: sidebar open-state is persisted (desktop). Reset on mobile entry so the
  // mobile drawer never auto-opens from a remembered desktop value.
  useEffect(() => {
    if (isMobile) closeDrawer();
  }, [isMobile, closeDrawer]);

  // Mobile drawer closes on navigation; the desktop in-flow sidebar stays expanded.
  const handleNavClick = isMobile ? closeDrawer : undefined;
  // Hover-to-preview groups only on the desktop panel (pointer devices); touch drawer uses click only.
  const enableHover = !isMobile && hasHover;

  const isServiceProvider = userRoles?.includes(UserRole.SERVICE_PROVIDER) ?? false;
  const isGuest = primaryRole === UserRole.GUEST;

  const spId = selectedServiceProviderId;

  const { data: spData } = useAccountServiceProviderQuery(spId ?? '');

  const spLogo = isServiceProvider && spId ? (spData?.logo?.link ?? null) : null;
  const spName = isServiceProvider && spId ? (spData?.name ?? null) : null;

  /**
   * Only the owner ever gets a padlocked nav item; for everyone else `verdictFor` answers
   * `hidden` and the destination drops out of the nav exactly as it used to.
   */
  const isPlanLocked = useCallback(
    (permissions: Permission[]) => verdictFor(permissions) === 'locked',
    [verdictFor],
  );

  const visibleGroups = useMemo(
    () =>
      getVisibleNavGroups({
        isServiceProvider,
        isGuest,
        hideDiscover: isGuestAudience,
        isMobile,
        can,
        canAny,
        vesselId: vessel?.id,
        isPlanLocked,
      }),
    [isServiceProvider, isGuest, isGuestAudience, isMobile, canAny, can, vessel?.id, isPlanLocked],
  );

  const location = useLocation();
  // const isInboxChatView =
  //   location.pathname.startsWith('/inbox/') &&
  //   location.pathname.split('/').filter(Boolean).length > 1;

  const currentLocationHandler = useCallback(
    (currentLocation: string) => {
      if (currentLocation === 'spending') {
        return (
          location.pathname === '/spending' ||
          location.pathname.startsWith('/spending/') ||
          location.pathname.startsWith('/expenses') ||
          location.pathname.startsWith('/transaction') ||
          location.pathname.startsWith('/new-transaction') ||
          location.pathname.startsWith('/invoices') ||
          location.pathname.startsWith('/invoice') ||
          location.pathname.startsWith('/quote') ||
          location.pathname.startsWith('/new-invoice')
        );
      }

      return (
        location.pathname === `/${currentLocation}` ||
        location.pathname.startsWith(`/${currentLocation}/`)
      );
    },
    [location.pathname],
  );

  // Accordion: only one group is open at a time. Until the user interacts,
  // the group containing the active route is shown open (derived fallback).
  const activeGroupKey = useMemo(
    () => visibleGroups.find((g) => g.items.some((i) => currentLocationHandler(i.key)))?.key,
    [visibleGroups, currentLocationHandler],
  );
  // Click pins a group open (survives mouse leave). Hover is transient preview.
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [hoverGroup, setHoverGroup] = useState<string | null>(null);
  const resolvedOpenGroup = hoverGroup ?? openGroup ?? activeGroupKey ?? null;
  const toggleGroup = useCallback((key: string) => {
    setOpenGroup((prev) => (prev === key ? null : key));
    setHoverGroup(null);
  }, []);

  const { unreadCount } = useNotificationContext();

  const FOOTER_ITEMS: FooterItem[] = useMemo(
    () => [
      {
        key: 'my-profile',
        label: 'My Profile',
        icon: <FiUser />,
        to: '/my-profile',
      },
      {
        key: 'settings',
        label: 'Settings',
        icon: <GoGear />,
        to: '/settings',
      },
      {
        key: 'faq',
        label: 'Help',
        icon: <AiOutlineQuestionCircle />,
        to: '/faq',
      },
    ],
    [unreadCount],
  );

  const menuContent = useMemo(
    () => (
      <div className={styles.sidebarContent}>
        <nav
          className={styles.mobileNav}
          onMouseLeave={enableHover ? () => setHoverGroup(null) : undefined}
        >
          {visibleGroups.map((group) => {
            const [firstItem] = group.items;
            if (group.standalone) {
              return (
                <NavLink
                  key={group.key}
                  className={clsx(
                    currentLocationHandler(firstItem.key) ? styles.active : styles.mobileNavItem,
                    group.startsSection && styles.navSectionStart,
                    firstItem.locked && styles.navItemLocked,
                  )}
                  to={firstItem.to}
                  onClick={handleNavClick}
                  onMouseEnter={enableHover ? () => setHoverGroup(null) : undefined}
                  title={firstItem.locked ? `${group.label} isn’t on your plan.` : undefined}
                >
                  {group.icon} <span className={styles.navLabel}>{group.label}</span>
                  {firstItem.locked && <FiLock className={styles.navLockIcon} aria-hidden />}
                </NavLink>
              );
            }
            const isOpen = resolvedOpenGroup === group.key;
            return (
              <div
                key={group.key}
                className={styles.navGroup}
                onMouseEnter={enableHover ? () => setHoverGroup(group.key) : undefined}
              >
                <button
                  type="button"
                  className={styles.navGroupHeader}
                  onClick={() => toggleGroup(group.key)}
                  aria-expanded={isOpen}
                >
                  <span className={styles.navGroupHeaderLeft}>
                    {group.icon}
                    <span className={styles.navLabel}>{group.label}</span>
                  </span>
                  <RiArrowDownSLine
                    className={`${styles.navGroupChevron} ${isOpen ? styles.navGroupChevronOpen : ''}`}
                  />
                </button>
                <div
                  className={`${styles.navGroupItemsWrap} ${
                    isOpen ? styles.navGroupItemsOpen : ''
                  }`}
                >
                  <div className={styles.navGroupItems}>
                    {group.items.map((item) => (
                      <NavLink
                        key={item.key}
                        className={clsx(
                          currentLocationHandler(item.key) ? styles.subItemActive : styles.subItem,
                          item.locked && styles.navItemLocked,
                        )}
                        to={item.to}
                        onClick={handleNavClick}
                        tabIndex={isOpen ? 0 : -1}
                        title={item.locked ? `${item.label} isn’t on your plan.` : undefined}
                      >
                        {item.icon} <span className={styles.navLabel}>{item.label}</span>
                        {item.locked && <FiLock className={styles.navLockIcon} aria-hidden />}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>
        <nav className={styles.sidebarFooter}>
          {FOOTER_ITEMS.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              className={clsx(
                styles.footerItem,
                styles.footerLink,
                currentLocationHandler(item.key) && styles.footerItemActive,
              )}
              aria-label={item.label}
              onClick={handleNavClick}
            >
              {item.icon} {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    ),
    [
      handleNavClick,
      currentLocationHandler,
      visibleGroups,
      resolvedOpenGroup,
      toggleGroup,
      FOOTER_ITEMS,
      enableHover,
    ],
  );

  const drawerContent = useMemo(
    () => (
      <>
        <aside
          className={`${styles.desktopSidebarPanel} ${styles.mobileSidebarPanel} ${
            isDrawerOpen ? styles.mobileSidebarPanelOpen : ''
          }`}
        >
          <div className={styles.drawerCloseRow}>
            <Link
              className={styles.logo}
              to="/dashboard"
              onClick={closeDrawer}
              aria-label="VesselHaven"
            >
              <img
                className={styles.logoInk}
                src={logoInk}
                alt=""
                aria-hidden="true"
                draggable={false}
              />
              <img
                className={styles.logoWhite}
                src={logoWhite}
                alt=""
                aria-hidden="true"
                draggable={false}
              />
            </Link>
          </div>
          {menuContent}
        </aside>
        {isDrawerOpen && (
          <div className={styles.mobileScrim} onClick={closeDrawer} aria-hidden="true" />
        )}
      </>
    ),
    [isDrawerOpen, closeDrawer, menuContent],
  );

  if (isMobile) {
    return <>{drawerContent}</>;
  }

  return (
    <DesktopSidebarRail
      onToggle={toggleDrawer}
      isOpen={isDrawerOpen}
      expanded
      menuContent={menuContent}
      currentLocationHandler={currentLocationHandler}
      groups={visibleGroups}
      openGroup={resolvedOpenGroup}
      onHoverGroup={setHoverGroup}
      onToggleGroup={toggleGroup}
      currentUser={currentUser}
      isServiceProvider={isServiceProvider}
      spLogo={spLogo}
      spName={spName}
      isGuest={isGuest}
    />
  );
};

export default Drawer;
